<template>
  <div class="overdue-actions-container">
    <div class="header-section">
      <h3 class="section-title">
        <i class="fas fa-exclamation-triangle"></i> Actions en retard
      </h3>
      <button @click="fetchRecommendations" class="refresh-btn" :disabled="loading">
        <i class="fas fa-sync" :class="{ 'fa-spin': loading }"></i>
        {{ loading ? 'Chargement...' : 'Actualiser' }}
      </button>
    </div>

    <div v-if="error" class="error-state">
      <i class="fas fa-exclamation-circle"></i> {{ error }}
      <button @click="fetchRecommendations" class="retry-btn">Réessayer</button>
    </div>

    <div v-else-if="loading && overdueActionsList.length === 0" class="loading-state">
      <div class="skeleton-row" v-for="n in 3" :key="n">
        <div class="skeleton-line w-30"></div>
        <div class="skeleton-line w-70"></div>
      </div>
      <div v-if="longLoading" class="long-wait">
        <i class="fas fa-info-circle"></i>
        <span>C’est un peu long…</span>
        <button class="retry-btn" @click="fetchRecommendations">Relancer</button>
      </div>
    </div>

    <div v-else-if="!loading && !error && overdueActionsList.length === 0" class="empty-state">
      <i class="fas fa-check-circle"></i> Aucune action en retard
    </div>

    <div v-else class="actions-list">
      <div 
        v-for="action in overdueActionsList" 
        :key="action.id"
        class="row action-card" >
        <div class="col-lg-6 card-header">
          <h6>{{ action.titre }}</h6>
        </div>

        <p class="action-description">
          {{ action.description }}
          <span v-if="action.statut" class="statut-badge">{{ action.statut }}</span>
        </p>

        <div class="col-lg-6">
          <button @click="markAsDone(action)" class="action-btn done-btn">
            <i class="fas fa-check"></i> Terminer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

export default {
  name: 'ListePriorisee',
  data() {
    return {
      recommendations: [],
      loading: false,
      error: null,
      longLoading: false,
      _loadingTimer: null
    };
  },
  computed: {
    overdueActionsList() {
      let actions = [];
      if (this.recommendations && this.recommendations.length > 0) {
        this.recommendations.forEach((item) => {
          const reco = item.recommandation;
          if (!reco) return;
          if (item.actions?.clients?.length) {
            item.actions.clients.forEach((action) => {
              if (this.isActionOverdue(action)) {
                actions.push(this.mapAction(action, reco, 'client'));
              }
            });
          }
          if (item.actions?.employes?.length) {
            item.actions.employes.forEach((action) => {
              if (this.isActionOverdue(action)) {
                actions.push(this.mapAction(action, reco, 'employe'));
              }
            });
          }
        });
      }
      // Optionally, sort actions here, e.g., by daysLate or priority
      // actions.sort((a, b) => b.daysLate - a.daysLate);
      return actions;
    }
  },
  created() {
    this.fetchRecommendations();
  },
  methods: {
    async fetchRecommendations() {
      this.loading = true;
      this.error = null;
      this.longLoading = false;
      clearTimeout(this._loadingTimer);
      // Déclenche un état "chargement long" après 6s pour proposer une action
      this._loadingTimer = setTimeout(() => { this.longLoading = true; }, 6000);
      
      try {
        if (!isAuthenticatedSession()) {
          this.error = "Session expirée. Veuillez vous reconnecter.";
          return;
        }

        const response = await axios.get("/api/raci/", { withCredentials: true });

        // Normaliser: le backend renvoie généralement { recommandations: [...] }
        const data = response?.data || {};
        this.recommendations = Array.isArray(data.recommandations) ? data.recommandations : (Array.isArray(data) ? data : []);
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401) {
          this.error = "Session expirée. Veuillez vous reconnecter.";
        } else if (status === 403) {
          this.error = "Accès non autorisé à ces données.";
        } else if (status === 404) {
          this.error = "Ressource introuvable (404).";
        } else if (status >= 500) {
          this.error = "Erreur serveur. Réessayez plus tard.";
        } else if (error.request && !error.response) {
          this.error = "Erreur de connexion au serveur";
        } else {
          this.error = error.response?.data?.detail || "Une erreur est survenue lors du chargement des tâches.";
        }
      } finally {
        this.loading = false;
        clearTimeout(this._loadingTimer);
        this.longLoading = false;
      }
    },

    isActionOverdue(action) {
      if (!action.date_echeance || action.statut === 'terminee') return false;
      const deadline = new Date(action.date_echeance);
      const today = new Date();
      return deadline < today;
    },

    mapAction(action, reco, type) {
      const deadline = action.date_echeance ? new Date(action.date_echeance) : null;
      const today = new Date();
      let daysLate = 0;
      if (deadline) {
        const d = Math.floor((today - deadline) / (1000 * 60 * 60 * 24));
        daysLate = d < 0 ? 0 : d;
      }

      return {
        id: action.id,
        recommandationId: reco.id,
        titre: action.description || 'Action sans titre',
        description: action.description || '',
        statut: action.statut,
        deadline,
        daysLate,
        recommandationTitre: reco.titre || 'Sans titre',
        actionType: type
      };
    },

    async markAsDone(action) {
      try {
        let apiUrl = `/api/`;

        if (action.actionType === 'client') {
          apiUrl += `raci-client/${action.id}/statut/`;
        } else if (action.actionType === 'employe') {
          apiUrl += `raci-conseiller/${action.id}/statut/`;
        } else {
          throw new Error("Type d'action inconnu pour la mise à jour du statut.");
        }

        await axios.patch(
          apiUrl, 
          {
            statut: 'terminee',
            date_cloture: new Date().toISOString().split('T')[0]
          },
          {
            withCredentials: true
          }
        );
        
        // Recharger les données après modification
        await this.fetchRecommendations();
      } catch (error) {
        this.handleError(error);
      }
    },

    handleError(error) {
      console.error("Erreur interceptée :", error);
      if (error.response?.status === 401) {
        console.error("Session expirée, veuillez vous reconnecter.");
        return;
      } else if (error.response) {
        this.error = error.response.data?.detail || `Erreur ${error.response.status}`;
      } else if (error.request) {
        this.error = "Erreur de connexion au serveur";
      } else {
        this.error = "Erreur inattendue";
      }
    }
  }
};
</script>

<style scoped>
/* .overdue-actions-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
} */

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-display), var(--font-primary);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.92);
}

.refresh-btn {
  padding: 8px 15px;
  border: 1px solid rgba(10, 26, 47, 0.14);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  color: rgba(10, 26, 47, 0.88);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  border-color: rgba(212, 175, 55, 0.45);
  box-shadow: 0 4px 12px rgba(10, 26, 47, 0.08);
}

.error-state {
  background: #fff0f0;
  color: var(--patrimonea-rouge);
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.retry-btn {
  margin-left: auto;
  padding: 5px 10px;
  background: var(--patrimonea-rouge);
  color: var(--patrimonea-blanc);
  border: none;
  border-radius: 4px;
}

.loading-state {
  padding: 8px 0;
}
.skeleton-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}
.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 37%, #f2f2f2 63%);
  background-size: 400% 100%;
  animation: shine 1.3s ease-in-out infinite;
}
.w-30 { width: 30%; }
.w-70 { width: 70%; }
.long-wait {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--bank-gray-600);
  margin-top: 6px;
}
@keyframes shine {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.statut-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  border-radius: 8px;
  background: rgba(10, 26, 47, 0.06);
  color: var(--bank-gray-600);
  border: 1px solid rgba(10, 26, 47, 0.08);
  vertical-align: middle;
}

.action-card {
  border-left: 4px solid rgba(212, 175, 55, 0.65);
  background: rgba(255, 255, 255, 0.75);
  border-top: 1px solid rgba(10, 26, 47, 0.06);
  border-right: 1px solid rgba(10, 26, 47, 0.06);
  border-bottom: 1px solid rgba(10, 26, 47, 0.06);
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 10px;
  box-shadow: var(--shadow-sm, 0 2px 10px rgba(10, 26, 47, 0.06));
}

.card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.priority-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
}

.days-late {
  color: var(--bank-danger);
  font-weight: bold;
}

.card-footer {
  display: flex;
  gap: 10px;
  margin-top: 1px;
}

.action-btn {
  padding: 8px 7px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.done-btn {
  background: linear-gradient(135deg, rgba(45, 134, 89, 0.95), rgba(45, 134, 89, 0.85));
  color: #fff;
  border: 1px solid rgba(45, 134, 89, 0.4);
}

.done-btn:hover:not(:disabled) {
  filter: brightness(1.05);
}

.details-btn {
  background: linear-gradient(135deg, rgba(10, 26, 47, 0.95), rgba(26, 34, 56, 0.92));
  color: #fff;
  border: 1px solid rgba(212, 175, 55, 0.35);
  text-decoration: none;
}

.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(359deg); }
}
</style>
